import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import { task, timeout } from 'ember-concurrency';

function parseVersion(versionString) {
  let [semver, commit] = versionString.split('_');
  let parts = semver.split('.').map(Number);
  return { major: parts[0] ?? 0, minor: parts[1] ?? 0, patch: parts[2] ?? 0, sha: commit };
}

function semverOf(versionString) {
  return versionString.split('_')[0];
}

function compareSemver(current, other) {
  let c = parseVersion(current);
  let o = parseVersion(other);

  for (let part of ['major', 'minor', 'patch']) {
    if (o[part] !== c[part]) return o[part] > c[part] ? 1 : -1;
  }

  return 0;
}

function diffType(current, latest) {
  let c = parseVersion(current);
  let l = parseVersion(latest);
  let order = compareSemver(current, latest);

  if (order < 0) return null;
  if (order === 0) return l.sha === c.sha ? null : 'sha';
  if (l.major > c.major) return 'major';
  if (l.minor > c.minor) return 'minor';
  return 'patch';
}

const DEFAULT_INTERVAL = 60_000;

// Read at import because the router drops these once the first transition settles.
const BOOT_PARAMS = new URLSearchParams(globalThis.location?.search ?? '');
const BOOT_REVISION = BOOT_PARAMS.get('revision');
const BOOT_PREVIEW_UPDATE = BOOT_PARAMS.get('preview-update');
const BOOT_FROM_UPDATE = BOOT_PARAMS.has('updated');

export default class VersionService extends Service {
  @service session;

  @tracked latestVersion = null;
  @tracked updateType = null;
  @tracked isPreviewBuild = Boolean(BOOT_REVISION) && !BOOT_FROM_UPDATE;

  constructor() {
    super(...arguments);
    let config = getOwner(this).resolveRegistration('config:environment');
    this.currentVersion = config.APP.version;
    this.versionUrl = config.APP.versionUrl;
    this.interval = config.APP.versionCheckInterval ?? DEFAULT_INTERVAL;

    if (BOOT_PREVIEW_UPDATE && config.environment === 'development') {
      this.previewUpdate(BOOT_PREVIEW_UPDATE);
    }
  }

  // Waiting on a real deploy is no way to work on the banner, so a development build can be told.
  previewUpdate(version) {
    this.latestVersion = version;
    this.updateType = diffType(this.currentVersion, version) ?? 'patch';
  }

  // A preview is built ahead of the activated revision, so it would be offered its own predecessor.
  startPolling() {
    if (this.isPreviewBuild) return;

    if (!this.poll.isRunning) {
      this.poll.perform();
    }
  }

  get updateAvailable() {
    return this.updateType !== null;
  }

  get isMajorUpdate() {
    return this.updateType === 'major';
  }

  get isMinorUpdate() {
    return this.updateType === 'minor';
  }

  get isPatchUpdate() {
    return this.updateType === 'patch';
  }

  get isShaUpdate() {
    return this.updateType === 'sha';
  }

  get currentSemver() {
    return semverOf(this.currentVersion);
  }

  get latestSemver() {
    return this.latestVersion ? semverOf(this.latestVersion) : null;
  }

  isNewerThanCurrent(version) {
    return Boolean(version) && compareSemver(this.currentVersion, version) > 0;
  }

  get currentSha() {
    return parseVersion(this.currentVersion).sha;
  }

  get latestSha() {
    return this.latestVersion ? parseVersion(this.latestVersion).sha : null;
  }

  reloadAppTask = task(async () => {
    window.location.assign(this.reloadUrl(window.location.href));
  });

  // Asking for the announced revision by name skips the API's cached index, so the reload lands on it.
  reloadUrl(href) {
    let url = new URL(href);
    if (this.latestVersion) {
      url.searchParams.set('revision', this.latestVersion);
      url.searchParams.set('updated', '1');
    }
    return url.toString();
  }

  // Once booted the pin has done its job, and a copied link should not stay on that revision.
  forgetPinnedRevision({ href = window.location.href, history = window.history } = {}) {
    let url = new URL(href);
    if (!url.searchParams.has('revision')) {
      return href;
    }
    url.searchParams.delete('revision');
    url.searchParams.delete('updated');
    history.replaceState(history.state, '', url.toString());
    return url.toString();
  }

  // Keeps asking after an update is found, so a release shipped before the reload replaces the offer.
  poll = task(async () => {
    while (true) {
      await timeout(this.interval);

      try {
        let response = await fetch(this.versionUrl);
        if (!response.ok) continue;
        let text = await response.text();
        let latest = text.trim();
        let type = diffType(this.currentVersion, latest);
        if (type && latest !== this.latestVersion) {
          this.latestVersion = latest;
          this.updateType = type;
        }
      } catch {
        // network blips are silent
      }
    }
  });
}
